import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { API, setToken, getToken, resolveUrl } from '../api';
import { safeStorage } from '../lib/safeStorage';

const StoreContext = createContext(null);
const money = value => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

export const useStore = () => {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used inside StoreProvider');
  return ctx;
};

export const getEffectivePrice = (product, settings) => {
  let price = product.price;
  let discountPercent = product.discountPercent || 0;
  if (settings?.saleActive && settings?.globalDiscountPercent) {
    discountPercent = Math.max(discountPercent, settings.globalDiscountPercent);
  }
  const finalPrice = money(price * (1 - discountPercent / 100));
  return { finalPrice, discountPercent, hasDiscount: discountPercent > 0 };
};

// combo -> product-like object for cart/UI
export const comboToProduct = (combo, products) => {
  const items = combo.productIds.map((id) => products.find((p) => p.id === id)).filter(Boolean);
  const original = items.reduce((s, p) => s + p.price, 0);
  return {
    id: combo.id, kind: 'combo', name: combo.name, tagline: combo.tagline, description: combo.description,
    size: `${items.length} × 5L`, image: combo.image ? resolveUrl(combo.image) : items[0]?.image, items,
    price: original, finalPrice: combo.price, active: combo.active,
    savings: Math.max(0, original - combo.price),
    savePercent: original ? Math.round(((original - combo.price) / original) * 100) : 0,
    stock: items.length ? Math.min(...items.map((p) => p.stock)) : 0,
  };
};

const resolveProduct = (p) => ({ ...p, image: resolveUrl(p.image) });
const resolveReview = (r) => ({ ...r, media: (r.media || []).map((m) => ({ ...m, url: resolveUrl(m.url) })) });

const CART_KEY = 'fbd_cart';
const readCart = () => {
  try { const cart = JSON.parse(safeStorage.getItem(CART_KEY)); return Array.isArray(cart) ? cart : []; } catch { return []; }
};

export const StoreProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [settings, setSettingsState] = useState({ shippingFee: 60, freeShippingAbove: 999, saleActive: true, saleText: '', globalDiscountPercent: 10, availableSizes: ['5L'] });
  const [reviews, setReviews] = useState([]);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState(false);
  const [combos, setCombos] = useState([]);
  const [cart, setCart] = useState(readCart);
  const [isStaff, setIsStaff] = useState(false);
  const [staffStatus, setStaffStatus] = useState('loading');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [publicReady, setPublicReady] = useState(false);
  const catalogRevision = useRef(0);
  const ordersRevision = useRef(0);

  useEffect(() => { safeStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);

  const checkStaff = useCallback(async () => {
    setStaffStatus(status => status === 'ready' ? status : 'loading');
    if (!getToken()) { setIsStaff(false); setStaffStatus('signed-out'); return; }
    try {
      await API.staffSession();
      setIsStaff(true);
      setStaffStatus('ready');
    } catch (error) {
      setIsStaff(false);
      setOrders([]);
      setStaffStatus(error.response?.status === 401 ? 'signed-out' : error.response?.status === 403 ? 'forbidden' : 'error');
    }
  }, []);
  useEffect(() => {
    checkStaff();
    const changed = event => { if (event.key === 'fbd_token' || event.key === null) checkStaff(); };
    window.addEventListener('storage', changed);
    window.addEventListener('focus', checkStaff);
    return () => { window.removeEventListener('storage', changed); window.removeEventListener('focus', checkStaff); };
  }, [checkStaff]);

  // initial load
  const loadPublic = useCallback(async () => {
    const revision = catalogRevision.current;
    try {
      const [p, s, r, c] = await Promise.all([API.getProducts(), API.getSettings(), API.getReviews(), API.getCombos()]);
      if (revision !== catalogRevision.current) return;
      setCombos(c);
      setProducts(p.map(resolveProduct));
      setSettingsState(s);
      setReviews(r.map(resolveReview));
      setLoadError(false);
      setPublicReady(true);
    } catch (e) {
      if (revision === catalogRevision.current) setLoadError(true);
      console.error('load error', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOrders = useCallback(async () => {
    const revision = ordersRevision.current;
    setOrdersLoading(true);
    try { const rows = await API.getOrders(); if (revision === ordersRevision.current) { setOrders(rows); setOrdersError(false); } }
    catch { if (revision === ordersRevision.current) setOrdersError(true); }
    finally { setOrdersLoading(false); }
  }, []);

  useEffect(() => { loadPublic(); }, [loadPublic]);
  useEffect(() => { if (isStaff) loadOrders(); }, [isStaff, loadOrders]);
  useEffect(() => {
    const refresh = () => {
      if (!navigator.onLine || document.hidden) return;
      loadPublic();
      if (isStaff) loadOrders();
    };
    const timer = setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    return () => { clearInterval(timer); window.removeEventListener('focus', refresh); window.removeEventListener('online', refresh); };
  }, [isStaff, loadPublic, loadOrders]);

  // ---- Cart ----
  const addToCart = useCallback((product, qty = 1) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) return prev.map((i) => (i.id === product.id ? { ...i, qty: Math.min(i.qty + qty, product.stock) } : i));
      return [...prev, { id: product.id, kind: product.kind || 'product', qty: Math.min(qty, product.stock) }];
    });
  }, []);
  const updateQty = useCallback((id, qty) => {
    setCart((prev) => (qty <= 0 ? prev.filter((i) => i.id !== id) : prev.map((i) => (i.id === id ? { ...i, qty } : i))));
  }, []);
  const removeFromCart = useCallback((id) => setCart((prev) => prev.filter((i) => i.id !== id)), []);
  const clearCart = useCallback(() => setCart([]), []);

  const cartDetailed = useMemo(() => {
    return cart.map((line) => {
      if (line.kind === 'combo') {
        const combo = combos.find((c) => c.id === line.id);
        if (!combo) return null;
        const cp = comboToProduct(combo, products);
        return { ...line, product: cp, unitPrice: cp.finalPrice, originalPrice: cp.price, discountPercent: cp.savePercent, lineTotal: money(cp.finalPrice * line.qty) };
      }
      const product = products.find((p) => p.id === line.id);
      if (!product) return null;
      const { finalPrice, discountPercent } = getEffectivePrice(product, settings);
      return { ...line, product, unitPrice: finalPrice, originalPrice: product.price, discountPercent, lineTotal: money(finalPrice * line.qty) };
    }).filter(Boolean);
  }, [cart, products, settings, combos]);

  const cartCount = useMemo(() => cart.reduce((s, i) => s + i.qty, 0), [cart]);

  const totals = useMemo(() => {
    const subtotal = money(cartDetailed.reduce((s, l) => s + l.lineTotal, 0));
    const originalSubtotal = money(cartDetailed.reduce((s, l) => s + l.originalPrice * l.qty, 0));
    const savings = money(originalSubtotal - subtotal);
    const shipping = subtotal === 0 ? 0 : subtotal >= settings.freeShippingAbove ? 0 : settings.shippingFee;
    return { subtotal, originalSubtotal, savings, shipping, total: money(subtotal + shipping) };
  }, [cartDetailed, settings]);

  // ---- Orders ----
  const placeOrder = useCallback(async (customer) => {
    const items = cartDetailed.map((l) => ({ id: l.id, name: l.product.name, size: l.product.size, qty: l.qty, unitPrice: l.unitPrice, image: l.product.image }));
    const order = await API.createOrder({ customer, items });
    await loadPublic(); // refresh stock
    if (isStaff) loadOrders();
    clearCart();
    return order;
  }, [cartDetailed, clearCart, loadPublic, isStaff, loadOrders]);

  const updateOrderStatus = useCallback(async (id, status) => {
    ordersRevision.current += 1;
    const updated = await API.updateOrderStatus(id, status);
    ordersRevision.current += 1;
    setOrders((prev) => prev.map((o) => (o.id === id ? updated : o)));
  }, []);

  // ---- Staff auth ----
  const staffLogout = useCallback(() => { setToken(null); safeStorage.removeItem('fbd_user'); setIsStaff(false); setStaffStatus('signed-out'); setOrders([]); }, []);

  // ---- Product management (staff) ----
  const updateProduct = useCallback(async (id, patch) => {
    catalogRevision.current += 1;
    const updated = await API.updateProduct(id, patch);
    catalogRevision.current += 1;
    setProducts((prev) => prev.map((p) => (p.id === id ? resolveProduct(updated) : p)));
    return resolveProduct(updated);
  }, []);

  // ---- Settings ----
  const setSettings = useCallback(async (next) => {
    catalogRevision.current += 1;
    const value = typeof next === 'function' ? next(settings) : next;
    const updated = await API.updateSettings(value);
    catalogRevision.current += 1;
    setSettingsState(updated);
    return updated;
  }, [settings]);

  // ---- Reviews ----
  const addReview = useCallback(async (review) => {
    catalogRevision.current += 1;
    const created = await API.addReview(review);
    catalogRevision.current += 1;
    setReviews((prev) => [resolveReview(created), ...prev]);
  }, []);
  const deleteReview = useCallback(async (id) => {
    catalogRevision.current += 1;
    await API.deleteReview(id);
    catalogRevision.current += 1;
    setReviews((prev) => prev.filter((r) => r.id !== id));
  }, []);

  // ---- Combos (staff) ----
  const createCombo = useCallback(async (c) => {
    catalogRevision.current += 1;
    const created = await API.createCombo(c);
    catalogRevision.current += 1;
    setCombos((prev) => [...prev, created]);
  }, []);
  const updateCombo = useCallback(async (id, c) => {
    catalogRevision.current += 1;
    const updated = await API.updateCombo(id, c);
    catalogRevision.current += 1;
    setCombos((prev) => prev.map((x) => (x.id === id ? updated : x)));
  }, []);
  const deleteCombo = useCallback(async (id) => {
    catalogRevision.current += 1;
    await API.deleteCombo(id);
    catalogRevision.current += 1;
    setCombos((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const value = {
    combos, createCombo, updateCombo, deleteCombo,
    products, settings, reviews, orders, cart, cartDetailed, cartCount, totals, isStaff, loading, loadError, publicReady, reloadStore: loadPublic,
    addToCart, updateQty, removeFromCart, clearCart, placeOrder, updateOrderStatus,
    staffStatus, checkStaff, staffLogout, updateProduct, setSettings, addReview, deleteReview, uploadFile: API.upload,
    ordersLoading, ordersError, reloadOrders: loadOrders,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};
